import React, { useCallback, useEffect, useState } from "react";

import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/pro-regular-svg-icons";

import { useTestCredentialById } from "src/api/agents/credentials";
import { Spinner } from "src/base-components/Spinner";
import type {
  CredentialItem,
  CredentialTestResult,
} from "src/clients/agent-management";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";

const formatDetailValue = (value: unknown): string => {
  if (value === null) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

type Props = {
  credential?: CredentialItem;
  onClose: () => void;
};

export const TestCredentialModal: React.FC<Props> = ({
  credential,
  onClose,
}) => {
  const testCredential = useTestCredentialById();
  const [result, setResult] = useState<CredentialTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runTest = useCallback(
    (credentialId: string) =>
      testCredential
        .mutateAsync({ credentialId })
        .then((testResult) => {
          setResult(testResult);
        })
        .catch((err: Error) => {
          setError(err?.message ?? "An unexpected error occurred");
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testCredential.mutateAsync],
  );

  useEffect(() => {
    if (credential) {
      setResult(null);
      setError(null);
      runTest(credential.id);
    }
  }, [credential, runTest]);

  const credentialName = credential?.name ?? "Unknown";

  const renderContent = () => {
    if (testCredential.isPending) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner />
          <p className="text-gray-600">
            Testing "{credentialName}" credentials...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center gap-4 py-4">
          <Icon icon={faTimesCircle} size="md" variant="error" />
          <div className="text-center">
            <p className="font-inter-semibold-14px text-gray-800">
              Test Failed
            </p>
            <p className="mt-2 text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      );
    }

    if (result) {
      return (
        <div className="flex flex-col items-center gap-4 py-4">
          <Icon
            variant={result.success ? "success" : "error"}
            icon={result.success ? faCheckCircle : faTimesCircle}
            size="md"
          />
          <div className="text-center">
            <p className="font-inter-semibold-14px text-gray-800">
              {result.success ? "Test Passed" : "Test Failed"}
            </p>
            <p className="mt-2 text-gray-600 text-sm">{result.message}</p>
            {result.details && Object.keys(result.details).length > 0 && (
              <div className="mt-4 rounded-lg bg-gray-50 p-4 text-left">
                <p className="mb-2 font-medium text-gray-600 text-xs uppercase">
                  Details
                </p>
                <dl className="space-y-1 text-sm">
                  {Object.entries(result.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <dt className="text-gray-500">{key}:</dt>
                      <dd className="text-gray-800">
                        {formatDetailValue(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal open={credential !== undefined} size="sm" onClose={onClose}>
      <Modal.Header>Test "{credentialName}" credentials</Modal.Header>
      <Modal.Content>{renderContent()}</Modal.Content>
      <Modal.Footer primaryButton={false} secondaryButton="Close" />
    </Modal>
  );
};
